// lib/vehicle/ulezChecker.js
//
// London ULEZ compliance worked out from a free DVLA vehicle record.
// Pure functions only: no network, no framework, no brand. Shared by
// functions/api/ulez-check.js, the ULEZ checker pages and the ServiceLad
// free vehicle page chip.
//
// The zone rules themselves:
//   Petrol cars and vans   Euro 4 or newer
//   Diesel cars and vans   Euro 6
//   Petrol motorbikes      Euro 3 or newer
//   Electric and hydrogen  always compliant
//   Over 3,500kg           London LEZ rules, not the standard ULEZ charge
//
// All VED maths stays in vehicleTax.js. All ULEZ maths stays here. Never
// copy either rule set into a page or a function.

import { classifyPoweredCycle } from "./vehicleType.js";

export const ULEZ_DAILY_CHARGE = 12.5;

/** Charging clean air zones that apply to ordinary private cars. */
export const CLEAN_AIR_ZONES = Object.freeze([
  Object.freeze({
    key: "london",
    name: "London ULEZ",
    area: "Every London borough",
    charge: "£12.50 a day",
    checkUrl: "https://tfl.gov.uk/modes/driving/ultra-low-emission-zone",
    checkLabel: "Transport for London",
  }),
  Object.freeze({
    key: "birmingham",
    name: "Birmingham Clean Air Zone",
    area: "Inside the A4540 ring road",
    charge: "£8 a day",
    checkUrl: "https://www.birmingham.gov.uk/cazcheck",
    checkLabel: "Birmingham City Council",
  }),
  Object.freeze({
    key: "bath",
    name: "Bath Clean Air Zone",
    area: "Central Bath",
    charge: "Cars are not charged",
    checkUrl: "https://www.bathnes.gov.uk/bath-clean-air-zone",
    checkLabel: "Bath and North East Somerset Council",
  }),
]);

function parseYear(value) {
  if (value == null || value === "") return null;
  const direct = Number(value);
  if (Number.isFinite(direct) && direct >= 1900 && direct <= 2100) return Math.floor(direct);
  const match = String(value).match(/(19|20)\d{2}/);
  if (!match) return null;
  const year = Number(match[0]);
  return year >= 1900 && year <= 2100 ? year : null;
}

// DVLA's euroStatus field is free text and is not always a Euro standard at
// all. It sometimes carries an EU vehicle category code (M1, N1, L3e), which
// says what kind of vehicle it is, not how clean it is. Those must be read as
// "no answer", never as "Euro 1".
function parseEuroNumber(raw) {
  if (!raw) return null;
  const text = String(raw).toUpperCase().trim();
  if (!text) return null;
  if (/UNKNOWN|UNSPECIFIED|NOT PROVIDED|NOT AVAILABLE/.test(text)) return null;
  if (/^[MNOLTRSG]\d/.test(text)) return null;

  if (/\bVI\b|EURO\s*VI/.test(text)) return 6;
  if (/\bV\b|EURO\s*V/.test(text)) return 5;
  if (/\bIV\b|EURO\s*IV/.test(text)) return 4;
  if (/\bIII\b|EURO\s*III/.test(text)) return 3;

  const digit = text.match(/([1-9])/);
  return digit ? Number(digit[1]) : null;
}

function normalisedFuel(value) {
  return String(value || "").toUpperCase().trim();
}

function isPetrol(fuel) {
  return fuel === "PETROL" || fuel.includes("PETROL");
}

function isDiesel(fuel) {
  return fuel === "DIESEL" || fuel.includes("DIESEL") || fuel === "HEAVY OIL";
}

function isZeroTailpipe(fuel) {
  return fuel === "ELECTRIC" || fuel === "ELECTRICITY" || fuel === "HYDROGEN" || fuel === "FUEL CELLS";
}

/**
 * Does this vehicle meet the London ULEZ standard?
 *
 * @param {string} fuelType DVLA fuel type
 * @param {string} euroStatus Euro emissions standard, if the record has one
 * @param {number|string} firstRegistration year, or any date containing one
 * @param {number|string} [co2Emissions] g/km, used only at the 2014 diesel boundary
 * @param {boolean} [isMotorcycle] motorbikes have a lower Euro 3 threshold
 * @returns {boolean}
 */
export function isUlezCompliant(fuelType, euroStatus, firstRegistration, co2Emissions, isMotorcycle) {
  const fuel = normalisedFuel(fuelType);
  if (!fuel) return false;

  const euro = parseEuroNumber(euroStatus);
  const year = parseYear(firstRegistration);

  // Zero tailpipe emissions
  if (isZeroTailpipe(fuel)) return true;

  if (isMotorcycle && isPetrol(fuel)) {
    if (euro != null) return euro >= 3;
    if (year != null) return year >= 2007;
    return false;
  }

  // Diesel (including diesel hybrids and diesel/electric)
  // Diesel is checked by year first, on purpose. Euro 6 became mandatory for
  // new registrations in September 2015, but plenty of 2014 cars were already
  // type approved to Euro 6, and emissions data at that boundary is often
  // recorded a standard behind. Trusting a stale "Euro 5" over the year would
  // tell an owner they owe £12.50 a day when they do not.
  if (isDiesel(fuel)) {
    if (year != null) {
      if (year >= 2015) return true;
      if (year === 2014) {
        if (euro != null && euro >= 6) return true;
        const co2 = co2Emissions == null || co2Emissions === "" ? null : Number(co2Emissions);
        if (co2 != null && Number.isFinite(co2) && co2 <= 130) return true;
        // Nothing on record either way. A 2014 diesel sits right on the
        // boundary, so the benefit of the doubt goes to the owner.
        if (euro == null) return true;
        return false;
      }
    }
    if (euro != null) return euro >= 6;
    return false;
  }

  // Petrol (including petrol hybrids and hybrid electric)
  if (isPetrol(fuel) || fuel.includes("HYBRID")) {
    if (euro != null) return euro >= 4;
    if (year != null) return year >= 2006;
    return false;
  }

  if (fuel === "GAS" || fuel === "LPG" || fuel === "CNG" || fuel.includes("GAS BI-FUEL")) {
    return euro != null && euro >= 4;
  }

  return false;
}

/**
 * True when we genuinely cannot tell, and should say so rather than guess.
 *
 * DVLA's free record often has no Euro standard on it, because it is not a
 * required field. For a petrol car first registered in 2005 that leaves a real
 * grey area: Euro 4 became compulsory for every new petrol registration in
 * January 2006, but individual models were approved to Euro 4 during 2005. With
 * £12.50 a day riding on the answer, "we cannot tell, check with TfL" beats a
 * confident guess.
 */
export function isUlezDataUnavailable(fuelType, euroStatus, firstRegistration, isMotorcycle) {
  if (isMotorcycle) return false;
  if (parseEuroNumber(euroStatus) != null) return false;
  const fuel = normalisedFuel(fuelType);
  if (!fuel) return true;
  if (isDiesel(fuel)) return false;
  if (!isPetrol(fuel) && !fuel.includes("HYBRID")) return false;
  return parseYear(firstRegistration) === 2005;
}

function requirementFor({ fuel, isMotorcycle, heavy }) {
  if (heavy) return "Vehicles over 3,500kg follow London LEZ rules, not the standard car and van ULEZ charge.";
  if (isZeroTailpipe(fuel)) return "Electric and hydrogen vehicles have no tailpipe emissions, so they are always compliant.";
  if (isDiesel(fuel)) {
    if (fuel.includes("HYBRID") || fuel.includes("ELECTRIC")) {
      return "Diesel hybrids must meet Euro 6, which normally means late 2015 or later.";
    }
    return "Diesel cars and vans need to be Euro 6, which normally means late 2015 or later.";
  }
  if (fuel.includes("HYBRID")) return "Hybrids are judged on their engine: petrol hybrids need Euro 4, diesel hybrids need Euro 6.";
  if (isMotorcycle && isPetrol(fuel)) return "Petrol motorbikes and mopeds need to be Euro 3 or newer.";
  if (isPetrol(fuel)) return "Petrol cars and vans need to be Euro 4 or newer, which normally means 2006 or later.";
  if (fuel === "GAS" || fuel === "LPG" || fuel === "CNG") return "Gas and LPG vehicles need to be Euro 4 or newer.";
  return "ULEZ depends on the certified Euro standard, the fuel and the vehicle class.";
}

/**
 * One verdict object for a DVLA shaped vehicle record.
 *
 * @param {object} vehicle DVLA fields: fuelType, euroStatus, co2Emissions,
 *   monthOfFirstRegistration, yearOfManufacture, typeApproval, wheelplan,
 *   revenueWeight, bodyType
 * @returns {{status: "compliant"|"charge"|"unknown"|"lez", label: string,
 *   headline: string, detail: string, requirement: string,
 *   dailyCharge: number|null, isMotorcycle: boolean, euroStandard: number|null,
 *   firstRegisteredYear: number|null}}
 */
export function checkUlez(vehicle = {}) {
  const fuel = normalisedFuel(vehicle.fuelType);
  const year = parseYear(vehicle.monthOfFirstRegistration) ?? parseYear(vehicle.yearOfManufacture);
  const euroStandard = parseEuroNumber(vehicle.euroStatus);
  const isMotorcycle = classifyPoweredCycle(vehicle) === "motorcycle";
  const weight = Number(vehicle.revenueWeight || 0);
  const heavy = Number.isFinite(weight) && weight > 3500;
  const requirement = requirementFor({ fuel, isMotorcycle, heavy });

  const base = {
    requirement,
    isMotorcycle,
    euroStandard,
    firstRegisteredYear: year,
    fuelType: vehicle.fuelType || null,
  };

  if (heavy) {
    return {
      ...base,
      status: "lez",
      label: "Check LEZ",
      headline: "This vehicle follows London LEZ rules",
      detail: "Over 3,500kg the Low Emission Zone applies instead of the standard ULEZ charge, and the daily amount is much higher. Check the exact position with Transport for London.",
      dailyCharge: null,
    };
  }

  if (!fuel) {
    return {
      ...base,
      status: "unknown",
      label: "Not known",
      headline: "We cannot tell from the free record",
      detail: "The free record does not show a fuel type for this registration, and fuel is what sets the Euro standard the vehicle has to meet.",
      dailyCharge: null,
    };
  }

  if (isUlezDataUnavailable(fuel, vehicle.euroStatus, year, isMotorcycle)) {
    return {
      ...base,
      status: "unknown",
      label: "Not known",
      headline: "We cannot tell from the free record",
      detail: "There is no Euro standard on this vehicle's record, and it was first registered in the year the petrol rules changed. Some 2005 petrol cars are Euro 4 and some are not, so check this one with Transport for London before you drive in.",
      dailyCharge: null,
    };
  }

  const compliant = isUlezCompliant(fuel, vehicle.euroStatus, year, vehicle.co2Emissions, isMotorcycle);

  if (compliant) {
    return {
      ...base,
      status: "compliant",
      label: "Free",
      headline: "No ULEZ charge for this vehicle",
      detail: `${requirement} This one meets it, so you can drive it in London without paying the daily charge.`,
      dailyCharge: 0,
    };
  }

  return {
    ...base,
    status: "charge",
    label: "Charge",
    headline: "A daily ULEZ charge is likely",
    detail: `${requirement} This one does not appear to meet it, so expect £12.50 for every day it is driven inside the zone.`,
    dailyCharge: ULEZ_DAILY_CHARGE,
  };
}
